import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Pagination } from '@mui/material'
import { FormattedMessage } from 'react-intl'

import { Preloader } from '../../../../components/UI'
import ReusableFilters from '../ReusableFilters/ReusableFilters'
import CommunityCard from './components/CommunityCard/CommunityCard'

import { isAuthenticated } from '../../../../helpers'
import useGetCommunity from './hooks/useGetCommunity'
import useGetAuthCommunity from './hooks/useGetAuthCommunity'

import { FILTER_CONFIG } from './config'

import styles from './styles.module.scss'

export const CommunityList = () => {
  const [pageCommunity, setPageCommunity] = useState(1)
  const [isAuthUser] = useState(isAuthenticated())
  const [defaultStatus, setDefaultStatus] = useState(null)

  const {
    communityList: publicCommunityList,
    isLoading: publicLoading,
    getCommunity,
  } = useGetCommunity()
  const {
    communityList: authCommunityList,
    isLoading: authLoading,
    getAuthCommunity,
  } = useGetAuthCommunity()

  const communityList = useMemo(
    () => (isAuthUser ? authCommunityList : publicCommunityList),
    [isAuthUser, authCommunityList, publicCommunityList]
  )
  const isLoading = useMemo(
    () => (isAuthUser ? authLoading : publicLoading),
    [isAuthUser, authLoading, publicLoading]
  )

  const handleChangeCommunities = useCallback((_, value) => {
    setPageCommunity(value)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const handleFiltersChange = useCallback(filters => {
    setDefaultStatus(filters.status)
  }, [])

  const getCommunities = useCallback(() => {
    const filters = {}

    // Only add allowIsPro filter if a specific status is selected (not "All")
    if (defaultStatus?.id !== null) {
      filters.allowIsPro = defaultStatus?.id
    }

    if (isAuthUser) {
      getAuthCommunity(pageCommunity, filters)
    } else {
      getCommunity(pageCommunity, filters)
    }
  }, [isAuthUser, pageCommunity, defaultStatus, getAuthCommunity, getCommunity])

  useEffect(() => {
    getCommunities()
  }, [getCommunities])

  const communities = useMemo(
    () => communityList?.communities || [],
    [communityList?.communities]
  )

  const defaultValues = useMemo(
    () => ({
      status: defaultStatus,
    }),
    [defaultStatus]
  )

  const paginationProps = useMemo(
    () => ({
      count: communityList?.paginator?.lastPage || 0,
      page: communityList?.paginator?.currentPage ?? 1,
      onChange: handleChangeCommunities,
      variant: 'outlined',
    }),
    [communityList?.paginator, handleChangeCommunities]
  )

  const showPagination = useMemo(
    () => communityList?.paginator?.lastPage > 1,
    [communityList?.paginator?.lastPage]
  )

  const showCommunitiesGrid = useMemo(
    () => communities.length > 0,
    [communities.length]
  )

  if (isLoading && !communityList) {
    return <Preloader />
  }

  return (
    <section className={styles.calendarList} data-testid='community-list'>
      <div className={styles.layoutContainer}>
        <div className={styles.sidebar} data-testid='community-filters-sidebar'>
          <ReusableFilters
            filterConfigs={FILTER_CONFIG}
            defaultValues={defaultValues}
            onFiltersChange={handleFiltersChange}
            isLoading={false}
          />
        </div>

        <div className={styles.content}>
          {showCommunitiesGrid ? (
            <div
              className={styles.communityContainer}
              data-testid='community-container'
            >
              {isLoading && (
                <div className={styles.filterLoaderOverlay}>
                  <Preloader />
                </div>
              )}

              <div
                className={styles.communitiesGrid}
                data-testid='communities-grid'
              >
                {communities.map(community => (
                  <CommunityCard {...community} key={community.id} />
                ))}
              </div>
            </div>
          ) : (
            <div
              className={styles.emptyData}
              data-testid='community-empty-state'
            >
              <FormattedMessage id='calendar.noCommunities' />
            </div>
          )}

          {showPagination && (
            <div
              className={styles.paginatorWrapper}
              data-testid='community-pagination'
            >
              <Pagination
                classes={{ ul: styles.pagination }}
                {...paginationProps}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default CommunityList
